import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {IDeadFeature, IMentalGameData, IMentalInitData} from "./MentalData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {StateMatrix} from "../utils/StateMatrix";
import {ReelEnhancerData} from "./ReelEnhancerData";
import {Mental} from "../Mental";
import {SymbolResizingWithInReelWrapper} from "../utils/SymbolResizingWithInReelWrapper";
import {MentalGameMode} from "./MentalGameMode";
import {MentalHelper} from "../utils/MentalHelper";
import {NoWinPresentation} from "../noWin/NoWinPresentation";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export class MentalModel extends GameModel<IMentalInitData, IMentalGameData> {
    private _visibleReelsMatrix: StateMatrix<string>;
    public isRestoreFeatureBet : boolean;
    public buyFeatureName : string;

    constructor(api: ApiPlugin) {
        super(api, false);
        api.events.once("featureBet", (data : boolean)=>{
           this.isRestoreFeatureBet = data;
        });
        api.events.once("featureName", (data : string)=>{
            this.buyFeatureName = data;
        });
        Mental.apiPlugin.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < Mental.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Mental.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });

        Mental.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < Mental.model.initData.featureBuyTimesBetValue.length; index++) {
                if (Mental.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });
    }

    private _reelEnhancerData: ReelEnhancerData;

    get reelEnhancerData(): ReelEnhancerData {
        return this._reelEnhancerData;
    }

    private static getSecondOccurrenceOfDeadSymbol(data: IMentalGameData): void {
        let second: boolean = false;
        for (let i: number = 0; i < data.deadSymPos.length; i++) {
            //  added this check to fix #68 -> do not count dead symbol pos if revealed from S2 or enhancer position
            if (data.deadSymPos[i] > -1 && data.evaluatedArea[i][data.deadSymPos[i]] !== "S2") {
                if (second) {
                    data.deadFeatureReelId = i;
                    break;
                }
                second = true;
            }
        }
    }

    private static getDeadSymbolPositions(data: IMentalGameData): number[] {
        const positions: number[] = [-1, -1, -1, -1, -1];
        for (let i: number = 0; i < data.evaluatedArea.length; i++) {
            for (let j: number = 0; j < data.fireFrameSymbolSize[i].length; j++) {
                if (data.deadSymbols[i][j]?.length) {
                    positions[i] = j;
                }
            }
        }
        return positions;
    }

    private static calculateCurrentWinRatioConfig(data: IMentalGameData): void {
        if (!data.betWayWins.length) {
            return;
        }

        // to set current win ratio in game
        const winRatio = data.totalBetWayWinnings / data.playedBetValue;
        let winLevels = Mental.WIN_RATIOS.normalWin.concat();
        winLevels = winLevels.concat(Mental.WIN_RATIOS.bigWin);
        for (let i: number = winLevels.length - 1; i >= 0; i--) {
            if (winRatio >= winLevels[i].ratio) {
                data.currentWinRatioConfig = {
                    duration: winLevels[i].duration,
                    ratio: winLevels[i].ratio,
                    text: winLevels[i].text,
                    type: winLevels[i].type
                };

                break;
            }
        }
        // to set if there is bigWin or not
        data.isBigWin = (winRatio >= Mental.WIN_RATIOS.bigWin[0].ratio);
    }

    private static calculateNumberOfFireFrames(data: IMentalGameData) {
        data.numberOfFireFrames = 0;
        for (let i = 0; i < data.fireFrames.length; i++) {
            for (let j = 0; j < data.fireFrames[i].length; j++) {
                if (data.fireFrames[i][j]) {
                    data.numberOfFireFrames++;
                }
            }
        }
    }

    /**
     * Adds processedReels array to game data that that is a 5x7 in size. So this function adds random padding symbols to make sure that the reels are infact this size.
     * @param data
     * @private
     */
    private static processReels(data: IMentalGameData) {
        data.processedReels = [];
        for (let reel of data.reels) {
            data.processedReels.push([...reel]);
        }
    }

    private static getVisibleReels(reels: string[][], paddingBefore: number, paddingAfter: number): string[][] {
        const result: string[][] = [];
        for (let reel of reels) {
            result.push(reel.slice(paddingBefore, -paddingAfter));
        }
        return result;
    }

    /**
     * function to reset multipositions,
     * will be using this check if multiplier already place on that position or not
     * @private
     */
    private static resetAllMultiplierPlacedPosition(data: IMentalGameData): boolean[][][] {
        const multiPlacedPositions: boolean[][][] = [];
        data.deadMultiplierPlacedValue = [];
        for (let reel: number = 0; reel < data.finalEvaluatedArea.length; reel++) {
            multiPlacedPositions[reel] = [];
            data.deadMultiplierPlacedValue[reel] = [];
            for (let symbol: number = 0; symbol < data.finalEvaluatedArea[reel].length; symbol++) {
                multiPlacedPositions[reel][symbol] = [];
                data.deadMultiplierPlacedValue[reel][symbol] = [];
                for (let k: number = 0; k < data.finalEvaluatedArea[reel][symbol].length; k++) {
                    for (let l = 0; l < data.finalEvaluatedArea[reel][symbol][k].size; l++) {
                        multiPlacedPositions[reel][symbol].push(false);
                        data.deadMultiplierPlacedValue[reel][symbol].push(1);
                    }

                }
            }
        }
        return multiPlacedPositions;
    }

    /**
     * function to create deadSymbols using final evaluated area.
     * @param data : IMentalGameData
     * @private
     */
    private static calculateDeadSymbolsData(data: IMentalGameData): IDeadFeature[][][] {
        const deadSymbols: IDeadFeature[][][] = [];
        for (let i: number = 0; i < data.finalEvaluatedArea.length; i++) {
            deadSymbols[i] = []; // reel
            for (let j: number = 0; j < data.finalEvaluatedArea[i].length; j++) {
                deadSymbols[i][j] = [];  // symbols
                for (let k: number = 0; k < data.finalEvaluatedArea[i][j].length; k++) {
                    // inner symbol
                    if (data.finalEvaluatedArea[i][j][k].wasDead && data.finalEvaluatedArea[i][j][k].name !== "D") {
                        deadSymbols[i][j].push({
                            symbol: data.finalEvaluatedArea[i][j][k].name,
                            multiplier: data.finalEvaluatedArea[i][j][k].multiplier
                        });
                    }
                }
            }
        }
        return deadSymbols;
    }

    // #112
    private static calculateBigWinAnticipation(mode: string, winAmountRatio: number): void {
        let r: number, rs: number, f: number;

        if (mode === MentalGameMode.NORMAL) {
            r = 10;
            f = 15;
        } else if (mode === MentalGameMode.STANDARD_FREESPIN) {
            r = 20;
            f = 20;
        } else if (mode === MentalGameMode.COOL_FREESPIN) {
            r = 50;
            f = 50;
        } else {
            r = 150;
            f = 150;
        }

        rs = MentalHelper.getRandomIntInRange(0, r, 0);

        Mental.isBigWinAnticipation = (winAmountRatio > (rs + f));
    }

    // #148
    private static findDeadMultiplier(deadSymbols: IDeadFeature[][][], valueToCheck: number): void {
        let isWin: boolean = false,
            symData: IDeadFeature;

        for (let i: number = 0; i < deadSymbols.length; i++) {
            for (let j: number = 0; j < deadSymbols[i].length; j++) {
                for (let k: number = 0; k < deadSymbols[i][j].length; k++) {
                    symData = deadSymbols[i][j][k];
                    if (symData.multiplier === valueToCheck) {
                        Mental.specialDeadMultiplierReveal = valueToCheck;

                        isWin = NoWinPresentation.getWinNoWinSymStatus(i, j, k);
                        if (isWin) {
                            Mental.specialDeadMultiplierRevealWin = true;

                            return;
                        }
                    }
                }
            }
        }

        if (Mental.specialDeadMultiplierReveal === valueToCheck) {
            Mental.specialDeadMultiplierRevealWin = false;
        }
    }

    protected onInit(data: IMentalInitData): void {
        super.onInit(data);
    }

    protected onGame(data: IMentalGameData): void {
        MentalModel.processReels(data);
        super.onGame(data);
        data.deadSymbols = MentalModel.calculateDeadSymbolsData(data);
        data.firstS2SymbolName = [];
        data.secondS2SymbolName = [];

        /**
         * indexes division of xWaysPlayed
         * 0 -> 2nd reel S2
         * 1 -> 4th reel S2
         * 2 -> 1st Enhancer
         * 3 -> 2nd Enhancer
         */
        data.xWaysPlayed = [false, false, false, false];

        MentalModel.calculateCurrentWinRatioConfig(data);
        this._reelEnhancerData = new ReelEnhancerData(data);
        MentalModel.calculateNumberOfFireFrames(data);
        this._visibleReelsMatrix = new StateMatrix<string>(MentalModel.getVisibleReels(data.processedReels, 2, 2));

        // will not change in any given scenario
        const fireFrameSymbolSizeInGame: number[][] = SymbolResizingWithInReelWrapper.getFireFrameSymbolSizeInGame(data.fireFrameSymbolSize, data.specialReelsOpen);
        data.fireFrameSymbolHeight = SymbolResizingWithInReelWrapper.inputSymbolSize(fireFrameSymbolSizeInGame);

        data.currentSymbolSize = JSON.parse(JSON.stringify(data.fireFrameSymbolSize));

        data.deadSymPos = MentalModel.getDeadSymbolPositions(data);
        MentalModel.getSecondOccurrenceOfDeadSymbol(data);
        data.deadMultiplierPlacedPositions = MentalModel.resetAllMultiplierPlacedPosition(data);

        // #112
        MentalModel.calculateBigWinAnticipation(data.mode, Math.round(data.totalBetWayWinnings / data.playedBetValue));

        // #148
        Mental.specialDeadMultiplierReveal = -1;
        Mental.specialDeadMultiplierRevealWin = false;
        MentalModel.findDeadMultiplier(data.deadSymbols, 9999);
        if (Mental.specialDeadMultiplierReveal !== 9999) {
            MentalModel.findDeadMultiplier(data.deadSymbols, 1000);
        }
    }
}